import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'
import Select, { components, InputActionMeta } from 'react-select'
import Creatable from 'react-select/creatable'
import { defaultProps, ISelectProps, ReactSelectProps, SelectStyleProps } from './interface'
import { createFilterFromAdditionalFields } from '../filters'
import type { CSSObject } from '@emotion/serialize'
import { evaluateExpression } from 'shared-libs/helpers/evaluation'
import { CustomFormulas } from 'shared-libs/helpers/formulas'
import apis from 'browser/app/models/apis'

import 'browser/components/atomic-elements/atoms/select/select/_react-select.scss'
import { translateString } from 'shared-libs/helpers/utils'

function getterFromPath(path: string) {
  if (!path) {
    return undefined
  }
  return (obj: any) => _.get(obj, path, undefined)
}

const REMOVAL_ACTIONS = ['remove-value', 'pop-value', 'deselect-option']

interface IReactSelectState {
  // In checkbox multiMode: holds the in-progress selection while the menu is open.
  // Committed to props.onChange once on menu close.
  draftValue?: any[]
}

export class ReactSelect extends React.Component<ISelectProps, IReactSelectState> {
  public static defaultProps = defaultProps

  private inputValue: string
  private select: Select
  private valueCache: any
  private _prevOptions: any[]
  private _prevOptionsBackup: any[]
  private MenuItemComponent: any
  // Stable Input component for checkbox mode blur guard (created once in constructor)
  private InputComponent: any
  // Stable default Option component for checkbox mode (used when no optionRenderer is provided)
  private CheckboxOptionComponent: any
  private _getValueCache: { input: [any, any]; output: any } | null = null
  // Memoize grouped options — invalidated when the raw options prop or inputValue changes
  private _groupOptionsCache: {
    rawOptions: any[]
    inputValue: string | undefined
    result: any[]
  } | null = null

  constructor(props) {
    super(props)
    this.state = {}
    this.valueCache = {}
    if (props.multiMode === 'checkbox') {
      this.MenuItemComponent = this.createCheckboxMenuItemComponent()
      this.InputComponent = this.createInputComponent()
      this.CheckboxOptionComponent = this.createCheckboxOptionComponent()
    } else {
      this.MenuItemComponent = this.createDefaultMenuItemComponent()
    }
  }

  public componentDidUpdate(prevProps: ISelectProps) {
    if (this.props.multiMode !== 'checkbox') {
      return
    }
    const wasOpen = prevProps.menuIsOpen
    const isNowOpen = this.props.menuIsOpen

    if (!wasOpen && isNowOpen) {
      const currentValue = _.isArray(this.props.value) ? [...(this.props.value as any[])] : []
      this.setState({ draftValue: currentValue })
    } else if (wasOpen && !isNowOpen && this.state.draftValue !== undefined) {
      const { draftValue } = this.state
      this.props.onChange?.(draftValue, draftValue)
      this.setState({ draftValue: undefined })
    }
  }

  // Allows callers to modify the in-progress draft while the menu is open,
  // e.g. when a newly created or edited entity needs to appear in the selection.
  public updateDraft = (updater: (current: any[]) => any[]) => {
    this.setState((state) => ({
      draftValue: updater(state.draftValue || []),
    }))
  }

  public focus() {
    this.select.focus()
  }

  private isLockedOption = (option) => {
    const { lockedValues, optionValuePath } = this.props
    if (_.isEmpty(lockedValues)) {
      return false
    }
    const candidate = optionValuePath ? _.get(option, optionValuePath) : option
    return _.some(lockedValues, (locked) => _.isEqual(locked, candidate))
  }

  private optionFromValue = (value) => {
    return this.valueCache[value] || this.buildValueFromId(value)
  }

  public render() {
    const {
      autoFocus,
      components,
      inputClassName,
      size,
      errorText,
      isCreatable,
      isDisabled,
      isEditableInline,
      isHorizontalLayout,
      optionLabelPath,
      optionValuePath,
      isStatic,
      multi,
      multiMode,
      valueRenderer,
      optionRenderer,
      formatOptionLabel,
      onOpen,
      onMenuClose,
      filterByFieldPaths,
      noOptionsMessage,
      isAsync,
      groupOptionsFn,
      groupOptionsFormula,
    } = this.props

    const isCheckboxMode = multiMode === 'checkbox'
    const noResultsText = this.getNoResultsText()
    const sizeClassName = _.isEmpty(size) ? '' : `c-select--${size}`
    const className = classNames('c-select', sizeClassName, inputClassName, {
      'c-select--error': !_.isEmpty(errorText),
      'c-select--isDisabled': isDisabled,
      'c-select--isEditableInline': isEditableInline,
      'c-select--isHorizontalLayout': isHorizontalLayout,
      'c-select--isStatic': isStatic,
      'c-select--isVerticalLayout': !isHorizontalLayout,
    })
    const options = this.getOptions()
    const menuPortalTarget = document.getElementsByTagName('body')[0]

    const selectProps: ReactSelectProps = {
      ...this.props,
      autofocus: autoFocus,
      className,
      disabled: isDisabled || isStatic,
      labelKey: optionLabelPath,
      getOptionLabel: getterFromPath(optionLabelPath),
      noResultsText,
      onChange: this.handleChange,
      onClose: this.handleClose,
      onInputChange: this.handleInputChange,
      options,
      ref: this.handleRef,
      tabIndex: isDisabled ? '-1' : '0',
      valueKey: optionValuePath,
      getOptionValue: getterFromPath(optionValuePath),
      styles: this.buildStyles(),
      classNamePrefix: 'reactSelect',
      components: {},
      isMulti: multi,
      menuPlacement: 'auto',
      menuPortalTarget,
      value: this.getEffectiveDisplayValue(options),
      onMenuOpen: this.handleMenuOpen,
      onMenuClose: onMenuClose,
      noOptionsMessage: noOptionsMessage ? (_) => noOptionsMessage : undefined,
    }

    if (isCheckboxMode) {
      // The visible search lives in the custom MenuList; suppress react-select's
      // native inputValue so the placeholder renders correctly.
      selectProps.inputValue = ''
      if (selectProps.closeMenuOnSelect === undefined) {
        selectProps.closeMenuOnSelect = false
      }
      if (selectProps.hideSelectedOptions === undefined) {
        selectProps.hideSelectedOptions = false
      }
    }

    if (groupOptionsFn || groupOptionsFormula) {
      selectProps.formatGroupLabel = this.formatGroupLabelFn
    }

    if (!_.isEmpty(components)) {
      selectProps.components = { ...components }
    }

    selectProps.components.MenuList = this.MenuItemComponent

    if (isCheckboxMode && !selectProps.components.Input) {
      selectProps.components.Input = this.InputComponent
    }

    if (valueRenderer) {
      selectProps.components.MultiValue = valueRenderer
      selectProps.components.SingleValue = valueRenderer
    }
    if (optionRenderer) {
      selectProps.components.Option = optionRenderer
    } else if (isCheckboxMode && !selectProps.components.Option) {
      selectProps.components.Option = this.CheckboxOptionComponent
    }
    if (filterByFieldPaths) {
      selectProps.filterOption = createFilterFromAdditionalFields(filterByFieldPaths)
    }
    if (formatOptionLabel) {
      selectProps.formatOptionLabel = formatOptionLabel
    }

    // DEBUG -- uncomment this to keep modal open for CSS / HTML inspection
    // selectProps.defaultMenuIsOpen = true

    if (isCreatable) {
      if (isAsync) {
        // In async mode the custom Create button in the MenuList footer handles creation.
        // Suppress Creatable's built-in option so it doesn't render as a blank row via
        // the entity optionRenderer.
        selectProps.isValidNewOption = () => false
      }
      return <Creatable {...selectProps} />
    } else {
      return <Select {...selectProps} />
    }
  }

  //////////////////////////////////////////////////////////////////////////////
  // Private Functions
  //////////////////////////////////////////////////////////////////////////////

  private createDefaultMenuItemComponent() {
    return (props) => {
      const { isCreatable, showCreatableWithResults, isAsync } = this.props
      const inputValue = this.inputValue
      const coreOptions = this._getOptionsHelper()
      const showCreateOption =
        isAsync &&
        ((isCreatable && _.isEmpty(coreOptions)) || showCreatableWithResults) &&
        !_.isEmpty(inputValue)
      const createNewOption = () => this.props.onNewOptionClick(this.inputValue)

      return (
        <>
          <components.MenuList {...props}>{props.children}</components.MenuList>
          {showCreateOption && (
            <div onClick={createNewOption} onTouchStart={createNewOption} className='c-select-createOption'>
              Create "{this.inputValue}"
            </div>
          )}
        </>
      )
    }
  }

  private createCheckboxMenuItemComponent() {
    return (props) => {
      const { isCreatable, showCreatableWithResults, isAsync, multi, allowSelectAll } = this.props

      const searchInputRef = React.useRef<HTMLInputElement>(null)
      const [searchValue, setSearchValue] = React.useState('')

      React.useEffect(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus()
          props.selectProps.onInputChange('', { action: 'input-change' })
        }
      }, [])

      // Re-focus the search input after each selection so the user can keep typing,
      // but only when the user hasn't tabbed focus into the options list themselves.
      const valueKey = Array.isArray(props.selectProps.value)
        ? (props.selectProps.value as any[]).length
        : props.selectProps.value
      React.useEffect(() => {
        const active = document.activeElement
        const isInMenu = active?.closest?.('[class*="reactSelect__menu"]')
        if (!isInMenu) {
          searchInputRef.current?.focus()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [valueKey])

      const inputValue = this.inputValue
      const coreOptions = this._getOptionsHelper()
      const showCreateOption =
        isAsync &&
        ((isCreatable && _.isEmpty(coreOptions)) || showCreatableWithResults) &&
        !_.isEmpty(inputValue)
      const createNewOption = () => this.props.onNewOptionClick(this.inputValue)
      const hasOptions = !_.isEmpty(coreOptions)

      const selectAllLabel = (() => {
        if (!multi || !allowSelectAll) return null
        const { optionValuePath } = this.props
        const flatOptions = this.isGroupedOptions(coreOptions)
          ? _.flatMap(coreOptions as any[], (g) => g.options)
          : coreOptions
        const effectiveDraft = this.state.draftValue ?? (_.isArray(this.props.value) ? (this.props.value as any[]) : [])
        const anySelected = flatOptions.some((opt) =>
          effectiveDraft.some((v) => _.get(v, optionValuePath) === _.get(opt, optionValuePath))
        )
        return anySelected ? 'Deselect All' : 'Select All'
      })()

      return (
        <>
          <div className='c-select-menuSearch'>
            <input
              ref={searchInputRef}
              type='text'
              placeholder='Search...'
              className='c-select-menuSearch__input'
              value={searchValue}
              onMouseDown={(e) => e.stopPropagation()}
              onChange={(e) => {
                const val = e.target.value
                setSearchValue(val)
                props.selectProps.onInputChange(val, { action: 'input-change' })
              }}
              onKeyDown={(e) => {
                if (e.key === 'Tab') {
                  e.stopPropagation()
                  return
                }
                e.stopPropagation()
              }}
              onBlur={() => {
                requestAnimationFrame(() => {
                  const active = document.activeElement
                  if (
                    active?.closest?.('[class*="reactSelect"]') ||
                    active?.closest?.('.c-select-menuSearch')
                  ) {
                    return
                  }
                  props.selectProps.onMenuClose?.()
                })
              }}
            />
            {multi && allowSelectAll && (
              <button
                type='button'
                onMouseDown={(e) => {
                  if (e.button !== 0) return
                  e.preventDefault()
                  if (hasOptions) this.handleSelectAll()
                }}
                onKeyDown={(e) => {
                  e.stopPropagation()
                  if ((e.key === 'Enter' || e.key === ' ') && hasOptions) {
                    e.preventDefault()
                    this.handleSelectAll()
                  }
                }}
                className={classNames('c-select-selectAll', { 'c-select-selectAll--disabled': !hasOptions })}
              >
                {selectAllLabel}
              </button>
            )}
          </div>
          <components.MenuList {...props}>{props.children}</components.MenuList>
          {showCreateOption && (
            <div onClick={createNewOption} onTouchStart={createNewOption} className='c-select-createOption'>
              Create "{this.inputValue}"
            </div>
          )}
        </>
      )
    }
  }

  private createInputComponent() {
    return (inputProps: any) => {
      // Intercept onBlur and suppress it when focus has moved to the in-menu search container.
      const { onBlur: rsOnBlur, ...restInputProps } = inputProps
      const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        requestAnimationFrame(() => {
          if (document.activeElement?.closest?.('.c-select-menuSearch')) {
            return
          }
          rsOnBlur?.(e)
        })
      }

      return (
        <span
          style={{
            overflow: 'hidden',
            opacity: 0,
            pointerEvents: 'none',
            width: 0,
          }}
        >
          <components.Input {...restInputProps} onBlur={onBlur} />
        </span>
      )
    }
  }

  private createCheckboxOptionComponent() {
    return (optionProps: any) => (
      <components.Option {...optionProps}>
        <div className='c-selectCheckboxOption'>
          <input
            type='checkbox'
            checked={optionProps.isSelected}
            readOnly
            className='c-selectCheckboxOption__checkbox'
            onKeyDown={(e) => {
              if (e.key === 'Tab') { e.stopPropagation(); return }
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                optionProps.selectOption(optionProps.data)
              }
            }}
          />
          <div className='c-selectCheckboxOption__content'>
            {optionProps.children}
          </div>
        </div>
      </components.Option>
    )
  }

  private formatGroupLabelFn = (group: any) => {
    const { multiMode, allowSelectAll, optionValuePath = '' } = this.props
    const isCheckboxMode = multiMode === 'checkbox'

    if (!allowSelectAll || !isCheckboxMode) {
      return (
        <div className='c-selectGroupLabel'>
          <span className='c-selectGroupLabel__label' title={group.label}>{group.label}</span>
        </div>
      )
    }

    const effectiveDraft = this.state.draftValue ?? (_.isArray(this.props.value) ? (this.props.value as any[]) : [])
    const anyInGroup = group.options.some((opt: any) =>
      effectiveDraft.some((v) => _.get(v, optionValuePath) === _.get(opt, optionValuePath))
    )
    return (
      <div className='c-selectGroupLabel'>
        <span className='c-selectGroupLabel__label' title={group.label}>{group.label}</span>
        <button
          type='button'
          className='c-selectGroupLabel__selectGroup'
          onMouseDown={(e) => {
            if (e.button !== 0) return
            e.preventDefault()
            this.handleToggleGroup(group.options, anyInGroup)
          }}
          onKeyDown={(e) => {
            e.stopPropagation()
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              this.handleToggleGroup(group.options, anyInGroup)
            }
          }}
        >
          {anyInGroup ? 'Deselect Group' : 'Select Group'}
        </button>
      </div>
    )
  }

  private handleToggleGroup = (groupOptions: any[], deselect: boolean) => {
    const { optionValuePath = '' } = this.props
    const currentDraft = this.state.draftValue ?? (_.isArray(this.props.value) ? [...(this.props.value as any[])] : [])
    if (deselect) {
      this.setState({
        draftValue: currentDraft.filter(
          (v) => !groupOptions.some((opt) => _.get(opt, optionValuePath) === _.get(v, optionValuePath))
        ),
      })
    } else {
      const toAdd = groupOptions.filter(
        (opt) => !currentDraft.some((v) => _.get(v, optionValuePath) === _.get(opt, optionValuePath))
      )
      this.setState({ draftValue: [...currentDraft, ...toAdd] })
    }
  }

  private handleSelectAll = () => {
    const { optionValuePath = '' } = this.props
    const coreOptions = this._getOptionsHelper()
    const flatOptions = this.isGroupedOptions(coreOptions)
      ? _.flatMap(coreOptions as any[], (g) => g.options)
      : coreOptions
    const currentDraft = this.state.draftValue ?? (_.isArray(this.props.value) ? [...(this.props.value as any[])] : [])
    const anySelected = flatOptions.some((opt) =>
      currentDraft.some((v) => _.get(v, optionValuePath) === _.get(opt, optionValuePath))
    )
    if (anySelected) {
      this.setState({
        draftValue: currentDraft.filter(
          (v) => !flatOptions.some((opt) => _.get(opt, optionValuePath) === _.get(v, optionValuePath))
        ),
      })
    } else {
      const toAdd = flatOptions.filter(
        (opt) => !currentDraft.some((v) => _.get(v, optionValuePath) === _.get(opt, optionValuePath))
      )
      this.setState({ draftValue: [...currentDraft, ...toAdd] })
    }
  }

  private getEffectiveDisplayValue(options: any[]) {
    const { multiMode } = this.props
    const { draftValue } = this.state
    if (multiMode === 'checkbox' && draftValue !== undefined) {
      // draftValue holds resolved objects; skip the getValue ID-resolution step
      return draftValue
    }
    return this.getValue(options)
  }

  private getValue(options) {
    const { multi, value } = this.props
    if (!value) {
      return value
    }
    if (multi) {
      // Return cached array when inputs are unchanged so react-select's
      // getDerivedStateFromProps sees a stable reference and skips resetting focusedOption.
      if (this._getValueCache?.input[0] === value && this._getValueCache?.input[1] === options) {
        return this._getValueCache.output
      }
      const result = _.map(value, (val) => this.getValueFromEntry(val, options))
      this._getValueCache = { input: [value, options], output: result }
      return result
    }
    return this.getValueFromEntry(value, options)
  }

  private getValueFromEntry(value, options) {
    const { optionValuePath } = this.props
    // backwards compatibility: some callers pass the full object
    if (_.isObject(value)) {
      return value
    }
    return (
      this.valueCache[value] ||
      _.find(options, (option) => _.get(option, optionValuePath) === value) ||
      this.buildValueFromId(value)
    )
  }

  private buildValueFromId(id: string) {
    const { optionValuePath, optionLabelPath } = this.props
    const value = {}
    _.set(value, optionValuePath, id)
    _.set(value, optionLabelPath, id)
    return value
  }

  private getOptions() {
    const { groupOptionsFn, groupOptionsFormula, showRemoveOption, removeOptionText, optionLabelPath, optionValuePath } = this.props
    let flatOptions = this._getOptionsHelper()

    if (!_.isEmpty(this.getValue(flatOptions)) && showRemoveOption) {
      const removeOption = {
        [optionLabelPath]: removeOptionText || 'Remove Value',
        [optionValuePath]: null,
      }
      flatOptions = [removeOption].concat(flatOptions)
    }

    if ((groupOptionsFn || groupOptionsFormula) && !this.isGroupedOptions(flatOptions)) {
      return this.applyGrouping(flatOptions)
    }

    return flatOptions
  }

  private applyGrouping(flatOptions: any[]): any[] {
    const { groupOptionsFn, groupOptionsFormula } = this.props
    const inputValue = this.inputValue
    // Use the raw options prop as the cache key so that translate()'s new-object-per-render
    // doesn't defeat memoization — if the prop reference is stable, the groups are stable.
    const rawOptions = this.props.options

    if (groupOptionsFn) {
      if (
        this._groupOptionsCache?.rawOptions === rawOptions &&
        this._groupOptionsCache?.inputValue === inputValue
      ) {
        return this._groupOptionsCache.result
      }
      const result = groupOptionsFn(flatOptions)
      this._groupOptionsCache = { rawOptions, inputValue, result }
      return result
    }
    if (groupOptionsFormula) {
      if (
        this._groupOptionsCache?.rawOptions === rawOptions &&
        this._groupOptionsCache?.inputValue === inputValue
      ) {
        return this._groupOptionsCache.result
      }
      const settings = apis.getSettings()
      const evalResult = evaluateExpression(
        { settings, ...CustomFormulas, options: flatOptions, searchFilter: inputValue },
        groupOptionsFormula
      )
      let result: any[]
      if (_.isArray(evalResult)) {
        result = evalResult
      } else if (_.isObject(evalResult)) {
        result = Object.entries(evalResult as object).map(([label, opts]) => ({ label, options: opts as any[] }))
      } else {
        return flatOptions
      }
      this._groupOptionsCache = { rawOptions, inputValue, result }
      return result
    }
    return flatOptions
  }

  private isGroupedOptions(options: any[]): boolean {
    return (
      options.length > 0 &&
      _.isObject(options[0]) &&
      'label' in options[0] &&
      'options' in options[0] &&
      _.isArray((options[0] as any).options)
    )
  }

  protected _getOptionsHelper() {
    const { multi, options, optionMapper } = this.props

    if (_.isEmpty(options)) {
      return []
    } else if (this.isGroupedOptions(options)) {
      return options
    } else if (multi) {
      return this.translate(options)
    } else if (optionMapper) {
      const computedOptions = optionMapper(options, this.inputValue)
      if (_.isEqual(this._prevOptionsBackup, computedOptions)) {
        return this._prevOptions
      }
      this._prevOptions = computedOptions
      this._prevOptionsBackup = _.clone(this._prevOptions)
      return this.translate(computedOptions)
    }
    return this.translate(options)
  }

  private translate(options) {
    const { optionLabelPath, frames } = this.props
    const translationTable = frames?.getContext('translationTable')
    return _.map(options, (opt) => {
      const translatedLabel = translateString(_.get(opt, optionLabelPath), translationTable)
      const translatedOpt = _.clone(opt)
      return _.set(translatedOpt, optionLabelPath, translatedLabel)
    })
  }

  private getNoResultsText() {
    const { isAsync, isLoading, loadingPlaceholder, noResultsText, options, searchPromptText } = this.props
    const inputValue = this.inputValue
    if (!isAsync) {
      return noResultsText
    }
    // return NoResultsText for Async select. react select has some internal logic
    // that display no results text when the internal filter returns empty even if
    // there are options from the server. This logic is unreliable but somehow
    // get works around that
    if (!_.isEmpty(options)) {
      return ''
    }
    if (_.isEmpty(inputValue)) {
      return searchPromptText
    }
    if (isLoading) {
      return loadingPlaceholder
    }
    return noResultsText
  }

  //////////////////////////////////////////////////////////////////////////////
  // Handlers
  //////////////////////////////////////////////////////////////////////////////

  private handleChange = (option, actionMeta = null) => {
    const { multiMode, isCreatable, multi, onNewOptionClick, onChange, optionValuePath } = this.props

    if (multi && multiMode === 'checkbox') {
      // Accumulate into draftValue
      if (actionMeta) {
        const { action, removedValue } = actionMeta
        const hasLockedValues = !_.isEmpty(this.props.lockedValues)
        if (hasLockedValues && _.includes(REMOVAL_ACTIONS, action) && this.isLockedOption(removedValue)) {
          return
        }
        if (action === 'clear' && hasLockedValues) {
          const lockedRawValues = (this.props.value || []).filter((val) =>
            _.some(this.props.lockedValues, (locked) => _.isEqual(locked, val))
          )
          option = _.isEmpty(lockedRawValues) ? [] : lockedRawValues
        }
      }
      const updated: any[] = option || []
      _.forEach(updated, (opt) => {
        _.set(this.valueCache, _.get(opt, optionValuePath), opt)
      })
      this.setState({ draftValue: updated })
      return
    }

    if (multi && actionMeta) {
      const { action, removedValue } = actionMeta
      const hasLockedValues = !_.isEmpty(this.props.lockedValues)
      if (hasLockedValues && _.includes(REMOVAL_ACTIONS, action) && this.isLockedOption(removedValue)) {
        return
      }
      if (action === 'clear' && hasLockedValues) {
        const lockedRawValues = (this.props.value || []).filter((val) =>
          _.some(this.props.lockedValues, (locked) => _.isEqual(locked, val))
        )
        if (!_.isEmpty(lockedRawValues)) {
          option = lockedRawValues.map((val) => this.optionFromValue(val))
        }
      }
    }

    if (_.isNil(option)) {
      onChange(null, null)
      return
    }
    if (isCreatable && option._isCreatableOption) {
      onNewOptionClick(option._label)
      return
    }

    let value = option
    if (optionValuePath) {
      if (multi) {
        value = this.props.value || []

        // support mutability by writing values from the `option` array into the
        // existing `value` array. This enables scenarios where a formula field
        // field can write an array reference to the same path as the select,
        // and the select then modifies it directly. Otherwise, the formula
        // field just overwrites whatever the new array coming from react-select
        // here, so the changes don't stick.

        // (not 100% sure if `option` is always non-nil and an array here,
        // erring on safe side)
        if (_.isArray(value)) {
          value.splice(0, value.length, ..._.map(option, optionValuePath))
        }
      } else {
        value = _.get(option, optionValuePath)
      }
    }

    // keep a local copy of the selected value(s)
    if (multi) {
      _.forEach(option, (opt) => {
        _.set(this.valueCache, _.get(opt, optionValuePath), opt)
      })
    } else {
      _.set(this.valueCache, value, option)
    }

    onChange(value, option, null, null, actionMeta)
  }

  private handleClose = () => {
    this.handleInputChange('')
  }

  private handleMenuOpen = () => {
    this.props.onOpen?.()
  }

  private handleInputChange = (input, actionMeta?: InputActionMeta) => {
    // **********
    // DEBUG - if you need to inspect a react select, put a breakpoint here to prevent it from closing on blur
    // **********
    
    // 'set-value' fires after every selection; 'input-blur' fires on blur — both are internal
    // housekeeping and must not trigger a consumer re-fetch.
    const isInternalReset =
      actionMeta?.action === 'set-value' || actionMeta?.action === 'input-blur'
    const { onInputChange } = this.props
    if (!isInternalReset && onInputChange) {
      onInputChange(input, actionMeta)
    }
    if (!isInternalReset) {
      this.inputValue = input
    }
  }

  private handleRef = (ref) => {
    const { onSelectRef } = this.props
    this.select = ref
    if (onSelectRef) {
      onSelectRef(ref)
    }
  }

  private buildStyles = () => {
    const { styles, validator } = this.props
    const allStyles: SelectStyleProps = {
      ...(styles || {}),
      menu: (base) => ({
        ...base,
        zIndex: 105,
        minWidth: '300px',
      }) as CSSObject,
      menuPortal: (base) => ({
        ...base,
        zIndex: 950,
      }) as CSSObject,
      control: (base) => ({
        ...base,
        borderWidth: 0,
        minHeight: '32px',
        outline: 'none',
        boxShadow: 'none',
        marginBottom: '1px',
      }) as CSSObject,
      valueContainer: (base) => ({
        ...base,
        paddingTop: 0,
        paddingBottom: 0,
      }) as CSSObject,
      dropdownIndicator: (base) => ({
        ...base,
        padding: '6px',
      }) as CSSObject,
      multiValue: (base, { data }) => {
        const valueStyles = { ...base }
        if (validator && !validator(data)) {
          valueStyles.backgroundColor = '#f4afae'
          valueStyles.borderColor = '#e53935'
        }
        return valueStyles as CSSObject
      },
      multiValueRemove: (base, { data }) => {
        if (this.isLockedOption(data)) {
          return { ...base, display: 'none' } as CSSObject
        }
        return base
      },
      clearIndicator: (base, { getValue }) => {
        const values = getValue?.() || []
        const allLocked = !_.isEmpty(values) && values.every((item) => this.isLockedOption(item))
        if (allLocked) {
          return { ...base, display: 'none' } as CSSObject
        }
        return base
      },
    }
    return _.mapValues(allStyles, (styleFunc, propertyName) => {
      if (styles && styles[propertyName]) {
        return (base, props) => {
          const defaultStyles = styleFunc(base, props)
          return styles[propertyName](defaultStyles, props)
        }
      } else {
        return styleFunc
      }
    })
  }
}
